import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import React, { FC, useEffect, useState } from "react";
import Modal from 'react-modal';
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import { Translator } from "../../contexts/translator";
import closeIcon from '../../images/close.svg';
import { PasswordConfig } from "../../models/SecurityPolicyPasswordConfig";
import { getErrorMessage } from "../../services/utilities/ServiceError";
import { IAppState } from "../../store/types";
import { SecurityPolicyHelper } from "../../utilities/securityPolicyPasswordConfigHelper";
import { requiredValidation, validateConfirmPassword, validateSecurityPolicyPassword } from "../../utilities/Validation";
import Label from "../label/label";
import {getErrorMessageToShow, initialValues} from "@/utilities/passwordUtil.tsx";

interface ChangeUserPasswordModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    onChangePassword: (values: any) => void;
    validateNewPassword: (value, translate: Translator) => void;
}

const ChangeUserPasswordModal: FC<ChangeUserPasswordModalProps> = ({ isOpen, setIsOpen, onChangePassword}) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [passwordConfig, setPasswordConfig] = useState<PasswordConfig>({
        enforceUppercase: false,
        enforceLowercase: false,
        enforceDigits: false,
        enforceSpecialCharacters: false,
        enforcePasswordMinLength: false,
        passwordMinLength: -1,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const fetchSecurityPolicyPasswordConfig = async () => {
        try {
            const securityPolicyPasswordConfig = await SecurityPolicyHelper.fetchSecurityPolicyPasswordConfig(user.securityPolicyId, translate);
            if (securityPolicyPasswordConfig) {
                setPasswordConfig(securityPolicyPasswordConfig);
            }
        }
        catch (error) {
            toast.warn(getErrorMessage(error));
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchSecurityPolicyPasswordConfig();
        }
    }, [isOpen]);

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName="flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white"
        className="bg-white border-none m-auto shadow select-none outline-none overflow-hidden">
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onChangePassword}>
            {
                ({ errors, values, touched }) => {
                    return <Form>
                        <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                            <div className="flex flex-row">
                                <p className="pl-3 font-bold font text-white">{translate.byKey(TranslationKeys.changePassword)}</p>
                            </div>
                            <img src={closeIcon} onClick={() => setIsOpen(false)} className="cursor-pointer mr-2" />
                        </div>
                        <div className="pt-3 pl-4 pr-0" style={{ width: '350px' }}>
                            {/* Currnt Password */}
                            <div className="flex flex-col flex-1 pr-3">
                                <Label htmlFor='currentPassword' className="text-xs text-gray-600 mt-1" required>
                                    {translate.byKey(TranslationKeys.password)}:
                                </Label>
                                <div className="flex flex-row justify-between">
                                    <Field
                                        name="currentPassword"
                                        type={showPassword ? 'text' : 'password'}
                                        validate={(value) => requiredValidation(value, translate)}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                    <div className="flex relative w-6 ml-1">
                                        <FontAwesomeIcon
                                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                            onClick={() => setShowPassword(showPassword => !showPassword)}
                                            icon={showPassword ? faEye : faEyeSlash} />
                                    </div>
                                </div>
                            </div>

                            {/* New Password */}
                            <div className="flex flex-col flex-1 pr-3 mt-2">
                                <Label htmlFor='newPassword' className="text-xs text-gray-600 mt-1" required>
                                    {translate.byKey(TranslationKeys.newPassword)}:
                                </Label>
                                <div className="flex flex-row justify-between">
                                    <Field
                                        name="newPassword"
                                        type={showNewPassword ? 'text' : 'password'}
                                        validate={(value) => validateSecurityPolicyPassword(value, passwordConfig, translate, true)}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                    <div className="flex relative w-6 ml-1">
                                        <FontAwesomeIcon
                                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                            onClick={() => setShowNewPassword(showNewPassword => !showNewPassword)}
                                            icon={showNewPassword ? faEye : faEyeSlash} />
                                    </div>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="flex flex-col flex-1 pr-3 mt-2">
                                <Label htmlFor='confirmPassword' className="text-xs text-gray-600 mt-1" required>
                                    {translate.byKey(TranslationKeys.confirmPassword)}:
                                </Label>
                                <div className="flex flex-row justify-between">
                                    <Field
                                        name="confirmPassword"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        validate={(value) => validateConfirmPassword(values.newPassword, value, translate)}
                                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input w-full" />
                                    <div className="flex relative w-6 ml-1">
                                        <FontAwesomeIcon
                                            className="absolute bottom-0 mb-1 right-0 cursor-pointer"
                                            onClick={() => setShowConfirmPassword(showConfirmPassword => !showConfirmPassword)}
                                            icon={showConfirmPassword ? faEye : faEyeSlash} />
                                    </div>
                                </div>
                            </div>

                            {getErrorMessageToShow(errors, touched)}
                        </div>
                        <div className="flex flex-row pb-3 justify-end bg-white">
                            <div className="flex flex-row mt-4 mr-5">
                                <button id='submitBtn' className="secondary secondary-enabled py-1 px-5" type='submit'>
                                    {translate.byKey(TranslationKeys.submit)}
                                </button>
                            </div>
                        </div>
                    </Form>
                }
            }
        </Formik>
    </Modal>
}

export default ChangeUserPasswordModal;
